import { useCallback, useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { fmtDate } from '../../shared/Panel';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import {
  EXECUTION_DEPARTMENT_CHOICES,
  FEATURE_EXECUTION_HINT,
  createMapping,
  departmentText,
  fetchMappingHistory,
  fetchMappings,
  fetchValues,
  removeMapping,
  updateMapping,
  type ExecutionDepartment,
  type ExecutionMappingChange,
  type ExecutionMappingRow,
  type ExecutionValueRow,
} from './executionDepartmentApi';

/**
 * „Kto realizuje wg TMS” (odpowiedzi Marathonu/Euvic 16.10.2026, pyt. 4a
 * i 30; przełącznik `executionDepartmentHint`).
 *
 * Pole TMS `order_execution_personel` („Dział odpowiedzialny”) bywa puste,
 * a gdy wpisana jest osoba, oznacza ona flotę albo Trade. Administrator
 * wpisuje tu, która wartość co znaczy — z powodem, każda zmiana idzie do
 * historii. Zlecenie z taką wartością dostaje znacznik „wg TMS: …” i filtr
 * listy. Tylko podpowiedź: do Trade przekazuje człowiek.
 *
 * Tabela „Wartości w zleceniach” to podpowiedź do wyboru — to, co TMS
 * faktycznie wpisuje, z liczbą zleceń. Ekran i API tylko dla sekcji
 * administracyjnej (wartości to osoby).
 */

export const FEATURE_OFF_NOTE =
  'Ekran „Kto realizuje wg TMS” jest wyłączony w wersji aplikacji, z której korzystasz.';

export const INTRO_NOTE =
  'Pole TMS „Dział odpowiedzialny” (osoba realizująca) mówi, czy zlecenie realizuje flota, czy Trade — zwykle jest puste. Wpisz, która wartość oznacza flotę, a która Trade. Zlecenia z taką wartością dostaną znacznik „wg TMS: …” na liście i karcie oraz filtr „Realizacja wg TMS”. To tylko podpowiedź — do Trade przekazuje człowiek.';

type Editing =
  | { mode: 'create'; value: string; department: ExecutionDepartment | '' }
  | { mode: 'update'; row: ExecutionMappingRow; department: ExecutionDepartment | '' }
  | { mode: 'remove'; row: ExecutionMappingRow };

export const ExecutionDepartmentList = () => {
  const on = useFeatureState(FEATURE_EXECUTION_HINT);
  if (on === undefined) return <FeaturesLoading />;
  if (!on) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel('execution-department-mappings')} />
        <Alert severity="info">{FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <ExecutionDepartmentScreen />;
};

const ExecutionDepartmentScreen = () => {
  const notify = useNotify();
  const [mappings, setMappings] = useState<ExecutionMappingRow[] | null>(null);
  const [values, setValues] = useState<ExecutionValueRow[] | null>(null);
  const [history, setHistory] = useState<ExecutionMappingChange[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, v, h] = await Promise.all([fetchMappings(), fetchValues(), fetchMappingHistory()]);
      setMappings(m);
      setValues(v);
      setHistory(h);
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const open = (e: Editing) => {
    setReason('');
    setEditing(e);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing.mode === 'create') {
        await createMapping({
          value: editing.value,
          department: editing.department as ExecutionDepartment,
          reason,
        });
      } else if (editing.mode === 'update') {
        await updateMapping(editing.row.id, {
          department: editing.department as ExecutionDepartment,
          reason,
        });
      } else {
        await removeMapping(editing.row.id, reason);
      }
      notify('Zapisano', { type: 'success' });
      setEditing(null);
      await load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const canSave =
    !!editing &&
    reason.trim() !== '' &&
    (editing.mode === 'remove' ||
      (editing.department !== '' && (editing.mode !== 'create' || editing.value.trim() !== '')));

  return (
    <Box sx={{ p: 2 }}>
      <Title title={menuLabel('execution-department-mappings')} />
      <Alert severity="info" sx={{ mb: 2 }} data-testid="execution-intro">
        {INTRO_NOTE}
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">Mapowanie</Typography>
        <Button variant="outlined" onClick={() => open({ mode: 'create', value: '', department: '' })}>
          Dodaj wartość
        </Button>
      </Stack>
      {mappings === null && !error ? (
        <CircularProgress size={24} />
      ) : (mappings ?? []).length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid="no-mappings">
          Brak mapowania — żadne zlecenie nie ma jeszcze znacznika „wg TMS”.
        </Typography>
      ) : (
        <Table size="small" aria-label="Mapowanie">
          <TableHead>
            <TableRow>
              <TableCell>Wartość pola w TMS</TableCell>
              <TableCell>Realizuje</TableCell>
              <TableCell>Ostatnia zmiana</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(mappings ?? []).map((m) => (
              <TableRow key={m.id} data-testid={`mapping-${m.id}`}>
                <TableCell>{m.value}</TableCell>
                <TableCell>{departmentText(m.department)}</TableCell>
                <TableCell>
                  {fmtDate(m.updatedAt)}
                  {m.updatedByName ? ` · ${m.updatedByName}` : ''}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      onClick={() => open({ mode: 'update', row: m, department: '' })}
                    >
                      Zmień
                    </Button>
                    <Button size="small" color="error" onClick={() => open({ mode: 'remove', row: m })}>
                      Usuń
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Wartości w zleceniach
      </Typography>
      {values === null && !error ? (
        <CircularProgress size={24} />
      ) : (values ?? []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Żadne zlecenie w bazie nie ma wypełnionego pola.
        </Typography>
      ) : (
        <Table size="small" aria-label="Wartości w zleceniach">
          <TableHead>
            <TableRow>
              <TableCell>Wartość pola w TMS</TableCell>
              <TableCell align="right">Zleceń</TableCell>
              <TableCell>Realizuje</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(values ?? []).map((v) => (
              <TableRow key={v.value}>
                <TableCell>{v.value}</TableCell>
                <TableCell align="right">{v.orders}</TableCell>
                <TableCell>{v.department ? departmentText(v.department) : 'bez mapowania'}</TableCell>
                <TableCell align="right">
                  {v.department ? null : (
                    <Button
                      size="small"
                      onClick={() => open({ mode: 'create', value: v.value, department: '' })}
                    >
                      Dodaj
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Historia zmian
      </Typography>
      {(history ?? []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Bez zmian.
        </Typography>
      ) : (
        <Table size="small" aria-label="Historia zmian">
          <TableHead>
            <TableRow>
              <TableCell>Kiedy</TableCell>
              <TableCell>Kto</TableCell>
              <TableCell>Wartość</TableCell>
              <TableCell>Zmiana</TableCell>
              <TableCell>Powód</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(history ?? []).map((h) => (
              <TableRow key={h.id}>
                <TableCell>{fmtDate(h.changedAt)}</TableCell>
                <TableCell>{h.changedByName ?? '—'}</TableCell>
                <TableCell>{h.value}</TableCell>
                <TableCell>
                  {departmentText(h.oldDepartment)} → {departmentText(h.newDepartment)}
                </TableCell>
                <TableCell>{h.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing?.mode === 'create'
            ? 'Dodaj wartość'
            : editing?.mode === 'update'
              ? `Zmień: ${editing.row.value}`
              : editing?.mode === 'remove'
                ? `Usuń: ${editing.row.value}`
                : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editing?.mode === 'create' && (
              <TextField
                label="Wartość pola w TMS"
                value={editing.value}
                onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                slotProps={{ htmlInput: { maxLength: 200 } }}
              />
            )}
            {editing && editing.mode !== 'remove' && (
              <TextField
                select
                label="Realizuje"
                value={editing.department}
                onChange={(e) =>
                  setEditing({ ...editing, department: e.target.value as ExecutionDepartment })
                }
              >
                {EXECUTION_DEPARTMENT_CHOICES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Powód"
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Anuluj</Button>
          <Button
            variant="contained"
            disabled={!canSave || saving}
            onClick={() => void save()}
            color={editing?.mode === 'remove' ? 'error' : 'primary'}
          >
            {editing?.mode === 'remove' ? 'Usuń' : 'Zapisz'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
